import { Box, Button, Typography, useTheme } from '@mui/material';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';

/**
 * Props for ProgressBarLeftElement component
 */
export interface ProgressBarLeftElementProps {
    steps: string[];
    activeTaskName?: string;
    onPrevious?: () => void;
    onNext?: () => void;
    hasPrevious?: boolean;
    isFetching?: boolean;
    customProgress?: { current: number; total: number };
    showPreviousOnly?: boolean;
    nextSectionName?: string;
    showProgress?: boolean;
}

/**
 * Left element component that provides responsive navigation for progress bars
 */
export default function ProgressBarLeftElement({
    steps,
    activeTaskName,
    onPrevious,
    onNext,
    hasPrevious,
    isFetching = false,
    customProgress,
    showPreviousOnly = false,
    showProgress = true,
    nextSectionName,
}: ProgressBarLeftElementProps) {
    const theme = useTheme();

    if (!activeTaskName) {
        return null;
    }

    // Use custom progress if provided, otherwise calculate from steps
    const progressText = customProgress
        ? `${customProgress.current} of ${customProgress.total}`
        : `${steps.indexOf(activeTaskName) + 1} of ${steps.length}`;

    return (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {hasPrevious &&
                (showPreviousOnly ? (
                    <Button
                        disabled={isFetching}
                        variant="text"
                        color="secondary"
                        onClick={onPrevious}
                        data-testid="progress-bar-previous-only-button"
                        sx={{
                            display: 'flex',
                            minWidth: 0,
                            p: 0,
                            width: '100%',
                            border: `1px solid ${theme.palette.cement[400]}`,
                            borderRadius: '4px',
                        }}>
                        <ChevronLeft />
                    </Button>
                ) : (
                    // Show original navigation buttons
                    <>
                        <Button
                            disabled={isFetching}
                            variant="text"
                            color="secondary"
                            onClick={onPrevious}
                            data-testid="progress-bar-back-button-mobile"
                            sx={{
                                display: { xs: 'flex', md: 'none' },
                                minWidth: 0,
                                p: 0,
                            }}>
                            <ChevronLeft />
                        </Button>
                        <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
                            <Button
                                disabled={isFetching}
                                variant="text"
                                color="secondary"
                                onClick={onPrevious}
                                data-testid="progress-bar-back-button-desktop"
                                sx={{
                                    border: `1px solid ${theme.palette.cement[400]}`,
                                    borderRight: 'none',
                                    borderRadius: '4px 0 0 4px',
                                    minWidth: 0,
                                    p: 0,
                                }}>
                                <ChevronLeft />
                            </Button>
                            <Button
                                disabled={isFetching}
                                variant="text"
                                color="secondary"
                                onClick={onNext}
                                data-testid="progress-bar-next-button-desktop"
                                sx={{
                                    border: `1px solid ${theme.palette.cement[400]}`,
                                    borderRadius: '0 4px 4px 0',
                                    minWidth: 0,
                                    p: 0,
                                }}>
                                <ChevronRight />
                            </Button>
                        </Box>
                    </>
                ))}
            {showProgress && (
                <Typography
                    variant="subtitle2"
                    component="span"
                    sx={{ display: { xs: 'none', md: 'block' }, whiteSpace: 'nowrap' }}>
                    {progressText}
                    {nextSectionName && (
                        <span style={{ marginLeft: 12, color: theme.palette.grey[400] }}>
                            Next up: {nextSectionName}
                        </span>
                    )}
                </Typography>
            )}
        </Box>
    );
}
